import React, { useMemo, useState } from 'react';
import { SearchIcon, FilterIcon, FileStackIcon, CheckCircle2Icon, AlertCircleIcon } from 'lucide-react';
import { Loan } from '../types/loan.types';
import { LoanListItem } from '../components/LoanListItem';
import { LoanDetailPanel } from '../components/LoanDetailPanel';
type FilterType = 'all' | 'incomplete' | 'complete';
// Mock data with varied completion states
const initialLoans: Loan[] = [{
  id: '1',
  borrowerName: 'Sarah Johnson',
  loanAmount: 425000,
  brokerageName: 'Premier Mortgage Group',
  loanNumber: 'LN-2024-001',
  propertyAddress: '123 Oak Street, Austin, TX 78701',
  requiredDocuments: [{
    id: '1a',
    name: 'Income Verification',
    status: 'approved',
    uploadedDate: 'Jan 15, 2024',
    fileName: 'income_verification.pdf'
  }, {
    id: '1b',
    name: 'Credit Report',
    status: 'approved',
    uploadedDate: 'Jan 16, 2024',
    fileName: 'credit_report.pdf'
  }, {
    id: '1c',
    name: 'Property Appraisal',
    status: 'pending_review',
    uploadedDate: 'Jan 18, 2024',
    fileName: 'appraisal.pdf'
  }, {
    id: '1d',
    name: 'Bank Statements',
    status: 'uploaded',
    uploadedDate: 'Jan 19, 2024',
    fileName: 'bank_statements.pdf'
  }]
}, {
  id: '2',
  borrowerName: 'Michael Chen',
  loanAmount: 680000,
  brokerageName: 'Coastal Lending Partners',
  loanNumber: 'LN-2024-002',
  propertyAddress: '456 Maple Ave, San Diego, CA 92101',
  requiredDocuments: [{
    id: '2a',
    name: 'Income Verification',
    status: 'uploaded',
    uploadedDate: 'Jan 20, 2024',
    fileName: 'w2_forms.pdf'
  }, {
    id: '2b',
    name: 'Credit Report',
    status: 'missing'
  }, {
    id: '2c',
    name: 'Property Appraisal',
    status: 'missing'
  }, {
    id: '2d',
    name: 'Bank Statements',
    status: 'missing'
  }]
}, {
  id: '3',
  borrowerName: 'Emily Rodriguez',
  loanAmount: 315000,
  brokerageName: 'Summit Financial Services',
  loanNumber: 'LN-2024-003',
  propertyAddress: '789 Pine Road, Denver, CO 80202',
  requiredDocuments: [{
    id: '3a',
    name: 'Income Verification',
    status: 'missing'
  }, {
    id: '3b',
    name: 'Credit Report',
    status: 'missing'
  }, {
    id: '3c',
    name: 'Property Appraisal',
    status: 'missing'
  }, {
    id: '3d',
    name: 'Bank Statements',
    status: 'missing'
  }]
}, {
  id: '4',
  borrowerName: 'David Thompson',
  loanAmount: 520000,
  brokerageName: 'Premier Mortgage Group',
  loanNumber: 'LN-2024-004',
  propertyAddress: '321 Elm Street, Portland, OR 97201',
  requiredDocuments: [{
    id: '4a',
    name: 'Income Verification',
    status: 'approved',
    uploadedDate: 'Jan 10, 2024',
    fileName: 'tax_returns.pdf'
  }, {
    id: '4b',
    name: 'Credit Report',
    status: 'approved',
    uploadedDate: 'Jan 11, 2024',
    fileName: 'credit_report.pdf'
  }, {
    id: '4c',
    name: 'Property Appraisal',
    status: 'approved',
    uploadedDate: 'Jan 14, 2024',
    fileName: 'appraisal_report.pdf'
  }, {
    id: '4d',
    name: 'Bank Statements',
    status: 'approved',
    uploadedDate: 'Jan 15, 2024',
    fileName: 'statements_q4.pdf'
  }]
}, {
  id: '5',
  borrowerName: 'Jessica Martinez',
  loanAmount: 395000,
  brokerageName: 'Coastal Lending Partners',
  loanNumber: 'LN-2024-005',
  propertyAddress: '654 Cedar Lane, Phoenix, AZ 85001',
  requiredDocuments: [{
    id: '5a',
    name: 'Income Verification',
    status: 'pending_review',
    uploadedDate: 'Jan 22, 2024',
    fileName: 'paystubs.pdf'
  }, {
    id: '5b',
    name: 'Credit Report',
    status: 'uploaded',
    uploadedDate: 'Jan 22, 2024',
    fileName: 'credit.pdf'
  }, {
    id: '5c',
    name: 'Property Appraisal',
    status: 'missing'
  }, {
    id: '5d',
    name: 'Bank Statements',
    status: 'pending_review',
    uploadedDate: 'Jan 23, 2024',
    fileName: 'bank_dec.pdf'
  }]
}, {
  id: '6',
  borrowerName: 'Robert Williams',
  loanAmount: 750000,
  brokerageName: 'Summit Financial Services',
  loanNumber: 'LN-2024-006',
  propertyAddress: '987 Birch Court, Seattle, WA 98101',
  requiredDocuments: [{
    id: '6a',
    name: 'Income Verification',
    status: 'approved',
    uploadedDate: 'Jan 8, 2024',
    fileName: 'income_docs.pdf'
  }, {
    id: '6b',
    name: 'Credit Report',
    status: 'approved',
    uploadedDate: 'Jan 9, 2024',
    fileName: 'credit_report.pdf'
  }, {
    id: '6c',
    name: 'Property Appraisal',
    status: 'approved',
    uploadedDate: 'Jan 12, 2024',
    fileName: 'appraisal.pdf'
  }, {
    id: '6d',
    name: 'Bank Statements',
    status: 'approved',
    uploadedDate: 'Jan 13, 2024',
    fileName: 'bank_statements.pdf'
  }]
}];
export function LoanDocumentDashboard() {
  const [loans, setLoans] = useState<Loan[]>(initialLoans);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [selectedLoanId, setSelectedLoanId] = useState<string>(initialLoans[0]?.id || '');
  const [pinnedLoanId, setPinnedLoanId] = useState<string | null>(null);
  const handleDocumentUpload = (loanId: string, documentId: string, file: File, fileName: string) => {
    setLoans(prevLoans => prevLoans.map(loan => {
      if (loan.id !== loanId) return loan;
      return {
        ...loan,
        requiredDocuments: loan.requiredDocuments.map(doc => {
          if (doc.id !== documentId) return doc;
          return {
            ...doc,
            status: 'uploaded' as const,
            fileName: fileName,
            uploadedDate: new Date().toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })
          };
        })
      };
    }));
  };
  const filteredLoans = useMemo(() => {
    return loans.filter(loan => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = searchQuery === '' || loan.borrowerName.toLowerCase().includes(searchLower) || loan.loanNumber.toLowerCase().includes(searchLower) || loan.brokerageName.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
      const isComplete = loan.requiredDocuments.every(doc => doc.status !== 'missing');
      if (filter === 'complete') return isComplete;
      if (filter === 'incomplete') return !isComplete;
      return true;
    });
  }, [loans, searchQuery, filter]);
  const stats = useMemo(() => {
    const total = loans.length;
    const complete = loans.filter(loan => loan.requiredDocuments.every(doc => doc.status !== 'missing')).length;
    const incomplete = total - complete;
    return {
      total,
      complete,
      incomplete
    };
  }, [loans]);
  const displayedLoan = loans.find(loan => loan.id === (pinnedLoanId || selectedLoanId));
  const handleSelectLoan = (loanId: string) => {
    setSelectedLoanId(loanId);
    if (!pinnedLoanId) {
      // Only change detail panel if nothing is pinned
    }
  };
  const handleTogglePin = (loanId: string) => {
    setPinnedLoanId(prev => prev === loanId ? null : loanId);
  };
  return <main className="min-h-screen w-full bg-gradient-to-br from-slate-50 to-blue-50" data-id="element-140">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10" data-id="element-141">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-142">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-143">
            <div data-id="element-144">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-145">
                Brokerage Loan Documents
              </h1>
              <p className="mt-1 text-gray-600" data-id="element-146">
                Upload required documents for each loan from your broker
                partners
              </p>
            </div>

            {/* Stats Pills */}
            <div className="flex items-center gap-3" data-id="element-147">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full" data-id="element-148">
                <FileStackIcon className="w-4 h-4 text-gray-500" data-id="element-149" />
                <span className="text-sm font-medium text-gray-700" data-id="element-150">
                  {stats.total} Loans
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full" data-id="element-151">
                <CheckCircle2Icon className="w-4 h-4 text-emerald-500" data-id="element-152" />
                <span className="text-sm font-medium text-emerald-700" data-id="element-153">
                  {stats.complete} Complete
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 rounded-full" data-id="element-154">
                <AlertCircleIcon className="w-4 h-4 text-amber-500" data-id="element-155" />
                <span className="text-sm font-medium text-amber-700" data-id="element-156">
                  {stats.incomplete} Pending
                </span>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="mt-6 flex flex-col sm:flex-row gap-4" data-id="element-157">
            <div className="relative flex-1 max-w-md" data-id="element-158">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-159" />
              <input type="search" placeholder="Search by borrower, loan number, or brokerage..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" aria-label="Search loans" data-id="element-160" />
            </div>

            <div className="flex items-center gap-2" role="group" aria-label="Filter loans" data-id="element-161">
              <FilterIcon className="w-4 h-4 text-gray-400 mr-1" aria-hidden="true" data-id="element-162" />
              {(['all', 'incomplete', 'complete'] as FilterType[]).map(filterType => <button key={filterType} onClick={() => setFilter(filterType)} className={`
                      px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                      ${filter === filterType ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 hover:border-gray-300'}
                    `} aria-pressed={filter === filterType} data-id="element-163">
                    {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
                  </button>)}
            </div>
          </div>
        </div>
      </header>

      {/* Master-Detail Layout */}
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-164">
        <div className="flex gap-6 h-[calc(100vh-280px)]" data-id="element-165">
          {/* Left Panel - Loan List (30%) */}
          <aside className="w-full md:w-[30%] flex-shrink-0" data-id="element-166">
            <div className="bg-gray-50 rounded-xl border border-gray-200 h-full overflow-hidden flex flex-col" data-id="element-167">
              <div className="p-4 border-b border-gray-200 bg-white" data-id="element-168">
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-169">Loans</h2>
                <p className="text-sm text-gray-600 mt-1" data-id="element-170">
                  {filteredLoans.length}{' '}
                  {filteredLoans.length === 1 ? 'loan' : 'loans'}
                </p>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-id="element-171">
                {filteredLoans.length === 0 ? <div className="text-center py-8 text-gray-500" data-id="element-172">
                    <FileStackIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" data-id="element-173" />
                    <p className="text-sm" data-id="element-174">No loans found</p>
                  </div> : filteredLoans.map(loan => <LoanListItem key={loan.id} loan={loan} isSelected={selectedLoanId === loan.id} isPinned={pinnedLoanId === loan.id} onClick={() => handleSelectLoan(loan.id)} onTogglePin={e => {
                e.stopPropagation();
                handleTogglePin(loan.id);
              }} data-id="element-175" />)}
              </div>
            </div>
          </aside>

          {/* Right Panel - Loan Detail (70%) */}
          <section className="flex-1 min-w-0" data-id="element-176">
            {displayedLoan ? <LoanDetailPanel loan={displayedLoan} isPinned={pinnedLoanId === displayedLoan.id} onTogglePin={() => handleTogglePin(displayedLoan.id)} onDocumentUpload={(documentId, file, fileName) => handleDocumentUpload(displayedLoan.id, documentId, file, fileName)} data-id="element-177" /> : <div className="bg-white rounded-xl shadow-lg border border-gray-200 h-full flex items-center justify-center" data-id="element-178">
                <div className="text-center text-gray-500" data-id="element-179">
                  <FileStackIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" data-id="element-180" />
                  <p className="text-lg font-medium" data-id="element-181">
                    Select a loan to view details
                  </p>
                </div>
              </div>}
          </section>
        </div>
      </div>
    </main>;
}